import React, { PropsWithChildren } from 'react'
import {
  View,
  Linking,
  StyleSheet,
  ImageBackground,
  TouchableOpacity,
} from 'react-native'

import { useNavigation } from '@react-navigation/native'
import { useTranslation } from 'react-i18next'

import Button from './Button'
import { EBookBanner } from './EBookBanner'
import Separator from '../components/Separator'
import SvgIcon from '../components/SvgIcon'
import Typography from '../components/Text/Typography'
import { useUser } from '../providers/UserProvider'
import {
  useWebLayout,
  SIDE_COLUMN_WIDTH,
  SIDE_COLUMN_CONTENT_WIDTH,
} from '../providers/WebLayoutProvider'
import GradientOverlay from '../screens/welcome/components/GradientOverlay'
import appStyles from '../styles/app-styles'
import Colors from '../styles/Colors'
import { MainStackNavigationType } from '../types/navigation-types'
import { convertHexToRGBA } from '../utils/colors'

const LEARN_MORE_LINK = 'https://relefant.eu/faq.html'
const ANDROID_STORE_LINK =
  'https://play.google.com/store/apps/details?id=com.relefant.app'
const IOS_STORE_LINK = 'https://apps.apple.com/us/app/relefant/id6462407658'

const BACKGROUND_COLOR = '#569874'
const GRADIENT_STOPS = [
  { color: BACKGROUND_COLOR, offset: 0, opacity: 1 },
  { color: '#73B993', offset: 1, opacity: 1 },
]

const openUrlOnPress = (url: string) => () => Linking.openURL(url)

const WebInfoColumn: React.FC<PropsWithChildren> = ({ children }) => {
  const { navigate } = useNavigation<MainStackNavigationType<'Root'>>()
  const { t } = useTranslation()
  const { account, user } = useUser()
  const showSubscriptionSection =
    !account || account.id === user?.managedGroupAccount?.id // personal account or groupAdmin

  const webLayout = useWebLayout()
  if (!webLayout.showRightColumn) return null

  return (
    <View style={styles.container}>
      <View style={styles.ebookBannerHolder}>
        <EBookBanner variant="large" />
      </View>
      <Separator height={18} />
      <View style={styles.howToSection}>
        <ImageBackground
          style={styles.howToOverlay}
          source={require('../assets/web-info-column/info-overlay.png')}
        />
        <Typography style={styles.howToTitle}>{t('webInfo.title')}</Typography>
        <Separator height={10} />
        <Typography
          style={styles.learnMoreButton}
          onPress={openUrlOnPress(LEARN_MORE_LINK)}>
          {t('webInfo.subtitle')}
        </Typography>
      </View>
      <Separator height={18} />
      <View style={styles.downloadSection}>
        <GradientOverlay gradientKey="app-info" stops={GRADIENT_STOPS} />
        <ImageBackground
          style={styles.downloadOverlay}
          source={require('../assets/web-info-column/welcome.png')}
        />
        <Typography weight="medium" style={styles.downloadTitle}>
          {t('webInfo.appTitle')}
        </Typography>
        <Separator height={8} />
        <Typography style={styles.downloadDescription}>
          {t('webInfo.appDescription')}
        </Typography>
        <Separator height={12} />
        <View style={appStyles.row}>
          <TouchableOpacity onPress={openUrlOnPress(IOS_STORE_LINK)}>
            <SvgIcon name="store-ios" />
          </TouchableOpacity>
          <Separator width={10} />
          <TouchableOpacity onPress={openUrlOnPress(ANDROID_STORE_LINK)}>
            <SvgIcon name="store-android" />
          </TouchableOpacity>
        </View>
      </View>

      <Separator height={18} />
      {showSubscriptionSection && (
        <View style={styles.section}>
          <Typography style={styles.sectionTitle}>
            {t('webInfo.subscriptionTitle')}
          </Typography>

          <Separator height={10} />
          <Typography style={styles.sectionDescription}>
            {t('webInfo.subscriptionFreeDescription')}
          </Typography>
          <Separator height={14} />
          <View style={styles.buttonHolder}>
            <Button
              text={t('webInfo.subscriptionButton')}
              style={styles.subscriptionsButton}
              onPress={() => navigate('Subscriptions')}
            />
          </View>
        </View>
      )}
      {children}
    </View>
  )
}

export default WebInfoColumn

const borderColor = convertHexToRGBA(Colors['layout.gray'], 0.5)
const styles = StyleSheet.create({
  container: {
    borderColor,
    paddingTop: 41,
    paddingLeft: 35,
    paddingRight: 93,
    minHeight: '100%',
    borderLeftWidth: 1,
    width: SIDE_COLUMN_WIDTH,
    backgroundColor: Colors['layout.white'],
  },
  ebookBannerHolder: { marginRight: -30 },
  howToSection: {
    paddingTop: 17,
    borderRadius: 10,
    paddingBottom: 19,
    paddingHorizontal: 24,
    width: SIDE_COLUMN_CONTENT_WIDTH,
    backgroundColor: Colors['layout.light'],
  },
  howToOverlay: {
    right: 0,
    bottom: 0,
    height: 110,
    position: 'absolute',
    width: SIDE_COLUMN_CONTENT_WIDTH,
  },
  howToTitle: {
    fontSize: 20,
    marginRight: '20%',
  },
  downloadSection: {
    height: 138,
    paddingTop: 16,
    paddingLeft: 65,
    borderRadius: 10,
    paddingRight: 14,
    paddingBottom: 14,
    overflow: 'hidden',
    width: SIDE_COLUMN_CONTENT_WIDTH,
    backgroundColor: BACKGROUND_COLOR,
  },
  downloadOverlay: {
    left: 0,
    bottom: 0,
    width: 74,
    height: 138,
    position: 'absolute',
  },
  downloadTitle: {
    fontSize: 18,
    lineHeight: 24,
    marginRight: '30%',
    color: Colors['layout.white'],
  },
  downloadDescription: {
    fontSize: 12,
    color: Colors['layout.white'],
  },
  section: {
    borderColor,
    borderTopWidth: 1,
    paddingVertical: 25,
  },
  sectionTitle: { fontSize: 20 },
  sectionDescription: { fontSize: 13 },
  learnMoreButton: {
    alignSelf: 'flex-start',
    color: Colors['brand.action'],
    textDecorationLine: 'underline',
  },
  buttonHolder: {
    marginRight: 'auto',
  },
  subscriptionsButton: {
    paddingVertical: 11,
    paddingHorizontal: 20,
  },
})
